// src/components/AccountMenu/FeedbackModal.jsx
// Modal for users to submit feedback, bug reports, and feature requests

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { saveFeedback } from '../../firebaseClient';
import './FeedbackModal.css';

const CATEGORIES = [
  { value: 'bug', label: 'Bug Report', icon: '🐛' },
  { value: 'idea', label: 'Feature Request', icon: '💡' },
  { value: 'other', label: 'Other Feedback', icon: '💬' }
];

export default function FeedbackModal({ isOpen, onClose }) {
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('other');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setError('Please enter your feedback');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await saveFeedback({
        message: message.trim(),
        category,
        email: email.trim() || null
      });

      setSubmitted(true);

      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('[FeedbackModal] Submit failed:', err);
      setError('Failed to send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setMessage('');
    setCategory('other');
    setEmail('');
    setSubmitted(false);
    setError('');
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <button className="feedback-close-btn" onClick={handleClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {submitted ? (
          <div className="feedback-success">
            <div className="feedback-success-icon">✅</div>
            <h2>Thank You!</h2>
            <p>Your feedback has been received. We appreciate you taking the time to help us improve VivaJour.</p>
          </div>
        ) : (
          <>
            <div className="feedback-header">
              <h2>Send Feedback</h2>
              <p>Help us improve VivaJour by sharing your thoughts</p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Category Selection */}
              <div className="feedback-categories">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`feedback-category ${category === cat.value ? 'active' : ''}`}
                    onClick={() => setCategory(cat.value)}
                  >
                    <span className="category-icon">{cat.icon}</span>
                    <span className="category-label">{cat.label}</span>
                  </button>
                ))}
              </div>

              {/* Message Input */}
              <div className="feedback-field">
                <label htmlFor="feedback-message">Your feedback</label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={
                    category === 'bug'
                      ? "Describe the issue you encountered..."
                      : category === 'idea'
                      ? "Tell us about your feature idea..."
                      : "Share your thoughts with us..."
                  }
                  rows={5}
                  maxLength={2000}
                  autoFocus
                />
                <div className="feedback-char-count">
                  {message.length}/2000
                </div>
              </div>

              {/* Optional Email */}
              <div className="feedback-field">
                <label htmlFor="feedback-email">
                  Email <span className="optional">(optional, for follow-up)</span>
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="feedback-error">{error}</div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="feedback-submit-btn"
                disabled={submitting || !message.trim()}
              >
                {submitting ? (
                  <>
                    <span className="feedback-spinner" />
                    Sending...
                  </>
                ) : (
                  'Send Feedback'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
